import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, vModelText as _vModelText, withDirectives as _withDirectives, withModifiers as _withModifiers, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "whatsapp-header__text" }
const _hoisted_2 = { class: "whatsapp-header__icon-title" }
const _hoisted_3 = { class: "whatsapp-header__icon-container" }
const _hoisted_4 = {
  key: 0,
  class: "whatsapp-field"
}
const _hoisted_5 = {
  key: 1,
  class: "whatsapp-field"
}
const _hoisted_6 = { class: "whatsapp-field" }
const _hoisted_7 = {
  key: 0,
  class: "whatsapp-attendee-tags"
}
const _hoisted_8 = { class: "whatsapp-attendee-tag-text" }
const _hoisted_9 = ["onClick"]
const _hoisted_10 = { class: "whatsapp-field" }
const _hoisted_11 = { class: "whatsapp-ai-row" }
const _hoisted_12 = ["disabled", "title"]
const _hoisted_13 = ["disabled", "title"]

import { Button, Input, Modal, Select } from "@/components/ui";
import type { Channel } from "@/dtos/Unipile.dto";
import type { CreateChatRequest } from "@/dtos/Unipile.dto";
import type { SelectOption } from "@/components/ui/types";
import { Icon } from "@iconify/vue";
import AiRefinePopover from "@/components/socials/AiRefinePopover.vue";
import { computed, ref, watch } from "vue";
import AIGenerationService from "@/services/ai/ai-generation";
import { useToast } from "@/composables/ui/useToast";
import { useResolvedAiPrompts } from "@/composables/useAiPrompts";

interface Props {
  show: boolean;
  channels: Channel[];
  defaultAccountId?: string;
  isLoading?: boolean;
  autoSetChannelType?: string;
  hideAccountSelection?: boolean;
  contactPhoneNumbers?: Array<{ type: string; number: string }>;
  initialMessage?: string;
  contactId?: string;
  icpId?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'StartWhatsAppChatModal',
  props: {
    show: { type: Boolean },
    channels: {},
    defaultAccountId: {},
    isLoading: { type: Boolean, default: false },
    autoSetChannelType: {},
    hideAccountSelection: { type: Boolean },
    contactPhoneNumbers: {},
    initialMessage: { default: "" },
    contactId: {},
    icpId: {}
  },
  emits: ["close", "create-chat"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const $toast = useToast();

// Filter channels to only non-email, connected channels
const availableChannels = computed(() => {
  let filtered = props.channels.filter(
    (channel) =>
      channel.type !== "email" &&
      channel.isConnected &&
      channel.accountId &&
      ["OK", "CREATION_SUCCESS", "RECONNECTED", "SYNC_SUCCESS"].includes(
        channel.status?.toUpperCase() || ""
      )
  );

  if (props.autoSetChannelType) {
    filtered = filtered.filter(
      (channel) => channel.type?.toLowerCase() === props.autoSetChannelType?.toLowerCase()
    );
  }

  return filtered;
});

// Account options for Select
const accountOptions = computed<SelectOption[]>(() => {
  return availableChannels.value.map((channel) => ({
    value: channel.accountId!,
    label: channel.name || "Unknown Account",
    icon: channel.icon || getChannelIcon(channel.type),
  }));
});

// Form data
const formData = ref<CreateChatRequest>({
  account_id: "",
  attendees_ids: [],
  text: "",
});

const attendeesInput = ref("");
const attendeeTags = ref<string[]>([]);
const selectedPhoneNumber = ref<string>("");
const showAiPanel = ref(false);
const showAiComposePanel = ref(false);
const isGeneratingAiCustom = ref(false);
const isGeneratingAiCompose = ref(false);
const aiRefineButtonRef = ref<HTMLElement | null>(null);
const aiComposeButtonRef = ref<HTMLElement | null>(null);
const resolvedAiPromptsQuery = useResolvedAiPrompts();

const toggleAiRefinePanel = () => {
  showAiComposePanel.value = false;
  showAiPanel.value = !showAiPanel.value;
};

const toggleAiComposePanel = () => {
  showAiPanel.value = false;
  showAiComposePanel.value = !showAiComposePanel.value;
};

const handleAiComposeGenerate = async (payload: { customInstructions: string }) => {
  if (isGeneratingAiCompose.value) return;
  isGeneratingAiCompose.value = true;
  try {
    const response = await AIGenerationService.generateCompose({
      contactId: props.contactId || "",
      icpId: props.icpId,
      channel: "whatsapp",
      customInstructions: payload.customInstructions,
      aiPromptCompose: resolvedAiPromptsQuery.composeAiPrompt.value,
    });

    if (response.body) {
      formData.value.text = response.body;
      showAiComposePanel.value = false;
      $toast?.success("Message composed successfully");
    }
  } catch (error: any) {
    console.error("Failed to compose message:", error);
    $toast?.error(error.message || "Failed to compose message");
  } finally {
    isGeneratingAiCompose.value = false;
  }
};

const handleAiCustomizationGenerate = async (payload: { customInstructions: string }) => {
  if (isGeneratingAiCustom.value) return;
  isGeneratingAiCustom.value = true;
  try {
    const response = await AIGenerationService.generateRefine({
      contactId: props.contactId || "",
      icpId: props.icpId,
      channel: "whatsapp",
      currentDraft: formData.value.text || "",
      customInstructions: payload.customInstructions,
    });

    if (response.body) {
      formData.value.text = response.body;
      showAiPanel.value = false;
      $toast?.success("Message refined successfully");
    }
  } catch (error: any) {
    console.error("Failed to refine message:", error);
    $toast?.error(error.message || "Failed to refine message");
  } finally {
    isGeneratingAiCustom.value = false;
  }
};

const errors = ref<{
  account_id?: string;
  attendees_ids?: string;
}>({});

const selectedAccount = computed(() => {
  if (!formData.value.account_id) return null;
  return availableChannels.value.find((channel) => channel.accountId === formData.value.account_id);
});

const recipientsPlaceholder = computed(() => {
  if (selectedAccount.value?.type === "linkedin")
    return "Enter LinkedIn profile IDs (comma-separated)";
  if (selectedAccount.value?.type === "whatsapp")
    return "Enter WhatsApp phone numbers (comma-separated)";
  return "Enter recipient IDs (comma-separated)";
});

const recipientsHelperText = computed(() => {
  if (selectedAccount.value?.type === "linkedin") {
    return "Enter LinkedIn profile IDs (e.g., ACwAAAAAAABD)";
  }
  if (selectedAccount.value?.type === "whatsapp") {
    return "Enter phone numbers in international format (e.g., +14155552671)";
  }
  return "Enter recipient IDs separated by commas";
});

// Check if form can be submitted
const canSubmit = computed(() => {
  return (
    formData.value.account_id &&
    formData.value.attendees_ids.length > 0 &&
    !errors.value.account_id &&
    !errors.value.attendees_ids
  );
});

// Handle attendees input change
const handleAttendeesInput = () => {
  // Parse comma-separated values
  const values = attendeesInput.value
    .split(",")
    .map((v) => v.trim())
    .filter((v) => v.length > 0);

  // Update tags
  attendeeTags.value = values;

  // Update form data
  formData.value.attendees_ids = values;

  // Validate
  if (values.length === 0 && attendeesInput.value.trim().length > 0) {
    errors.value.attendees_ids = "Please enter at least one recipient ID";
  } else {
    errors.value.attendees_ids = undefined;
  }
};

// Remove attendee tag
const removeAttendeeTag = (index: number) => {
  attendeeTags.value.splice(index, 1);
  formData.value.attendees_ids = [...attendeeTags.value];
  attendeesInput.value = attendeeTags.value.join(", ");
};

// Get channel icon
const getChannelIcon = (type: string): string => {
  switch (type?.toLowerCase()) {
    case "linkedin":
      return "mdi:linkedin";
    case "whatsapp":
      return "mdi:whatsapp";
    default:
      return "mdi:message-outline";
  }
};

// Handle form submission
const handleSubmit = () => {
  // Reset errors
  errors.value = {};

  // Validate account
  if (!formData.value.account_id) {
    errors.value.account_id = "Please select an account";
    return;
  }

  // Validate attendees
  if (formData.value.attendees_ids.length === 0) {
    errors.value.attendees_ids = "Please enter at least one recipient ID";
    return;
  }

  // Prepare request
  const request: CreateChatRequest = {
    account_id: formData.value.account_id,
    attendees_ids: formData.value.attendees_ids,
  };

  if (formData.value.text?.trim()) {
    request.text = formData.value.text.trim();
  }

  emit("create-chat", request);
};

// Reset form when modal closes
watch(
  () => props.show,
  (isShowing) => {
    if (!isShowing) {
      // Reset form
      formData.value = {
        account_id: props.defaultAccountId || "",
        attendees_ids: [],
        text: "",
      };
      attendeesInput.value = "";
      attendeeTags.value = [];
      selectedPhoneNumber.value = "";
      showAiPanel.value = false;
      showAiComposePanel.value = false;
      errors.value = {};
    } else {
      if (props.defaultAccountId) {
        formData.value.account_id = props.defaultAccountId;
      }
      if (props.initialMessage) {
        formData.value.text = props.initialMessage;
      }
    }
  }
);

const phoneNumberOptions = computed<SelectOption[]>(() => {
  if (!props.contactPhoneNumbers || props.contactPhoneNumbers.length === 0) {
    return [];
  }
  return props.contactPhoneNumbers.map((phone) => ({
    value: phone.number,
    label: `${phone.type}: ${phone.number}`,
  }));
});

const handlePhoneNumberSelect = (value: string | number) => {
  const phoneNumber = String(value);
  selectedPhoneNumber.value = phoneNumber;
  attendeesInput.value = phoneNumber;
  handleAttendeesInput();
};

watch(
  () => props.defaultAccountId,
  (newAccountId) => {
    if (newAccountId && props.show) {
      formData.value.account_id = newAccountId;
    }
  }
);

// Watch for autoSetChannelType to auto-set account
watch(
  () => [props.autoSetChannelType, props.show, availableChannels.value],
  () => {
    if (props.autoSetChannelType && props.show && availableChannels.value.length > 0) {
      const matchingChannel = availableChannels.value.find(
        (channel) => channel.type?.toLowerCase() === props.autoSetChannelType?.toLowerCase()
      );
      if (matchingChannel?.accountId) {
        formData.value.account_id = matchingChannel.accountId;
      } else if (props.defaultAccountId) {
        formData.value.account_id = props.defaultAccountId;
      }
    }
  },
  { immediate: true }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Modal), {
    show: __props.show,
    size: "md",
    dialogMaxWidth: "640px",
    closeOnBackdrop: false,
    blurBackdrop: false,
    backdropOpacity: 0.5,
    zIndex: 10060,
    "show-close-button": false,
    onClose: _cache[8] || (_cache[8] = ($event: any) => (_ctx.$emit('close')))
  }, {
    header: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createVNode(_unref(Icon), {
              icon: "mdi:whatsapp",
              class: "whatsapp-header__icon"
            })
          ]),
          _cache[9] || (_cache[9] = _createElementVNode("div", null, [
            _createElementVNode("h2", { class: "whatsapp-header__title" }, "Start New WhatsApp Chat"),
            _createElementVNode("p", { class: "whatsapp-header__subtitle" }, " Create a new conversation with one or more recipients ")
          ], -1))
        ])
      ]),
      _createElementVNode("button", {
        type: "button",
        class: "whatsapp-header__close",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('close'))),
        "aria-label": "Close"
      }, [
        _createVNode(_unref(Icon), {
          icon: "mdi:close",
          width: "18",
          height: "18"
        })
      ])
    ]),
    body: _withCtx(() => [
      _createElementVNode("form", {
        id: "whatsapp-chat-form",
        onSubmit: _withModifiers(handleSubmit, ["prevent"]),
        class: "whatsapp-form"
      }, [
        (!props.hideAccountSelection)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _createVNode(_unref(Select), {
                modelValue: formData.value.account_id,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formData.value.account_id) = $event)),
                label: "Account",
                placeholder: "Select an account",
                options: accountOptions.value,
                error: errors.value.account_id,
                required: ""
              }, null, 8, ["modelValue", "options", "error"])
            ]))
          : _createCommentVNode("", true),
        (
            props.autoSetChannelType === 'whatsapp' &&
            props.contactPhoneNumbers &&
            props.contactPhoneNumbers.length > 0
          )
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createVNode(_unref(Select), {
                modelValue: selectedPhoneNumber.value,
                "onUpdate:modelValue": [
                  _cache[2] || (_cache[2] = ($event: any) => ((selectedPhoneNumber).value = $event)),
                  handlePhoneNumberSelect
                ],
                label: "Select Phone Number",
                placeholder: "Choose a phone number",
                options: phoneNumberOptions.value
              }, null, 8, ["modelValue", "options"])
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_6, [
          _createVNode(_unref(Input), {
            modelValue: attendeesInput.value,
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((attendeesInput).value = $event)),
            label: "Recipients",
            placeholder: recipientsPlaceholder.value,
            error: errors.value.attendees_ids,
            "helper-text": recipientsHelperText.value,
            required: "",
            onInput: handleAttendeesInput
          }, null, 8, ["modelValue", "placeholder", "error", "helper-text"]),
          (attendeeTags.value.length > 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(attendeeTags.value, (tag, index) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: index,
                    class: "whatsapp-attendee-tag"
                  }, [
                    _createElementVNode("span", _hoisted_8, _toDisplayString(tag), 1),
                    _createElementVNode("button", {
                      type: "button",
                      class: "whatsapp-attendee-tag-remove",
                      onClick: ($event: any) => (removeAttendeeTag(index))
                    }, [
                      _createVNode(_unref(Icon), { icon: "mdi:close" })
                    ], 8, _hoisted_9)
                  ]))
                }), 128))
              ]))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_10, [
          _cache[11] || (_cache[11] = _createElementVNode("label", { class: "whatsapp-label" }, [
            _createTextVNode(" Message "),
            _createElementVNode("span", { class: "whatsapp-optional" }, "(Optional)")
          ], -1)),
          _withDirectives(_createElementVNode("textarea", {
            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((formData.value.text) = $event)),
            class: "whatsapp-textarea",
            placeholder: "Enter your message here...",
            rows: "6"
          }, null, 512), [
            [_vModelText, formData.value.text]
          ]),
          _createElementVNode("div", _hoisted_11, [
            _createElementVNode("button", {
              ref_key: "aiComposeButtonRef",
              ref: aiComposeButtonRef,
              type: "button",
              class: "whatsapp-ai-btn whatsapp-ai-btn--compose",
              disabled: isGeneratingAiCompose.value,
              title: isGeneratingAiCompose.value ? 'Generating...' : 'AI Compose',
              onClick: toggleAiComposePanel
            }, [
              _createVNode(_unref(Icon), {
                icon: "mdi:sparkles",
                class: "whatsapp-ai-btn-icon"
              }),
              _createElementVNode("span", null, _toDisplayString(isGeneratingAiCompose.value ? "Generating..." : "AI Compose"), 1)
            ], 8, _hoisted_12),
            _createElementVNode("button", {
              ref_key: "aiRefineButtonRef",
              ref: aiRefineButtonRef,
              type: "button",
              class: "whatsapp-ai-btn",
              disabled: isGeneratingAiCustom.value,
              title: isGeneratingAiCustom.value ? 'Generating...' : 'AI Refine',
              onClick: toggleAiRefinePanel
            }, [
              _createVNode(_unref(Icon), {
                icon: "mdi:sparkles",
                class: "whatsapp-ai-btn-icon"
              }),
              _cache[10] || (_cache[10] = _createElementVNode("span", null, "AI Refine", -1))
            ], 8, _hoisted_13)
          ]),
          _createVNode(AiRefinePopover, {
            show: showAiPanel.value,
            "trigger-el": aiRefineButtonRef.value,
            disabled: isGeneratingAiCustom.value,
            onGenerate: handleAiCustomizationGenerate,
            onClose: _cache[5] || (_cache[5] = ($event: any) => (showAiPanel.value = false))
          }, null, 8, ["show", "trigger-el", "disabled"]),
          _createVNode(AiRefinePopover, {
            show: showAiComposePanel.value,
            "trigger-el": aiComposeButtonRef.value,
            disabled: isGeneratingAiCompose.value,
            title: "AI Compose",
            "generate-button-label": "Generate Message",
            "show-attachments": false,
            onGenerate: handleAiComposeGenerate,
            onClose: _cache[6] || (_cache[6] = ($event: any) => (showAiComposePanel.value = false))
          }, null, 8, ["show", "trigger-el", "disabled"])
        ])
      ], 32)
    ]),
    footer: _withCtx(() => [
      _createVNode(_unref(Button), {
        variant: "ghost",
        size: "md",
        onClick: _cache[7] || (_cache[7] = ($event: any) => (_ctx.$emit('close'))),
        disabled: __props.isLoading
      }, {
        default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
          _createTextVNode(" Cancel ", -1)
        ]))]),
        _: 1
      }, 8, ["disabled"]),
      _createVNode(_unref(Button), {
        type: "submit",
        form: "whatsapp-chat-form",
        variant: "primary",
        size: "md",
        icon: "mdi:send",
        "icon-position": "left",
        loading: __props.isLoading,
        disabled: !canSubmit.value
      }, {
        default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
          _createTextVNode(" Create Chat ", -1)
        ]))]),
        _: 1
      }, 8, ["loading", "disabled"])
    ]),
    _: 1
  }, 8, ["show"]))
}
}

})